import axios from "axios"
import { API_URL } from "../utils/ApiLinks"
import type { RequestDto, RequestCountersDto, UpdateStatusDto, UpdateRequestDto } from "../types/crm/types/request"


interface CacheItem<T> {
  data: T
  timestamp: number
  key: string
}

class SimpleCache {
  private cache: Record<string, CacheItem<any>> = {}
  private readonly TTL: number = 60000

  set<T>(key: string, data: T): void {
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      key,
    }
  }

  get<T>(key: string): T | null {
    const item = this.cache[key]
    if (!item) return null

    if (Date.now() - item.timestamp > this.TTL) {
      delete this.cache[key]
      return null
    }

    return item.data as T
  }

  invalidate(key: string): void {
    delete this.cache[key]
  }

  invalidateAll(): void {
    this.cache = {}
  }
}

const cache = new SimpleCache()

class RequestStore {
  private requests: Record<number, RequestDto> = {}

  storeRequests(requestsList: RequestDto[]): void {
    requestsList.forEach((request) => {
      this.requests[request.id] = request
    })
  }

  getRequestById(id: number): RequestDto | null {
    return this.requests[id] || null
  }

  hasRequest(id: number): boolean {
    return !!this.requests[id]
  }

  clear(): void {
    this.requests = {}
  }
}

const requestStore = new RequestStore()

const getRequestsByBroker = async (brokerId: number): Promise<RequestDto[]> => {

  try {
    const url = `${API_URL}/requests/get-requests-by-broker/${brokerId}`

    const response = await axios.get<RequestDto[]>(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })

    requestStore.storeRequests(response.data)

    return response.data
  } catch (error: any) {
    console.error("Error en getRequestsByBroker:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    })
    handleRequestError(error)
    return []
  }
}

const getRequestsByClient = async (clientId: number): Promise<RequestDto[]> => {

  try {
    const url = `${API_URL}/requests/get-requests-by-client/${clientId}`

    const response = await axios.get<RequestDto[]>(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })

    requestStore.storeRequests(response.data)

    return response.data
  } catch (error: any) {
    console.error("Error en getRequestsByClient:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    })
    handleRequestError(error)
    return []
  }
}

const getRequestCounters = async (userProfileId: number, role: string): Promise<RequestCountersDto> => {

  const cacheKey = `counters_${userProfileId}_${role}`

  const cachedData = cache.get<RequestCountersDto>(cacheKey)
  if (cachedData) {
    return cachedData
  }

  const url = `${API_URL}/requests/get-request-counters/${userProfileId}/${role}`

  try {
    const response = await axios.get<RequestCountersDto>(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })
    if (response.data && typeof response.data.totalRequests === "number") {
      cache.set(cacheKey, response.data)
      return response.data
    } else {
      console.error("Estructura de respuesta inesperada:", response.data)
      const defaultData = {
        totalRequests: 0,
        inProgress: 0,
        rejected: 0,
        approved: 0,
      }
      cache.set(cacheKey, defaultData)
      return defaultData
    }
  } catch (error: any) {
    console.error("Error en getRequestCounters:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      request: error.request,
      config: error.config,
    })

    handleRequestError(error)
    const defaultData = {
      totalRequests: 0,
      inProgress: 0,
      rejected: 0,
      approved: 0,
    }
    return defaultData
  }
}

const getRequestById = async (requestId: number): Promise<RequestDto | null> => {

  if (requestStore.hasRequest(requestId)) {
    return requestStore.getRequestById(requestId)
  }


  try {
    const url = `${API_URL}/requests/get-request/${requestId}`

    const response = await axios.get<RequestDto>(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })
    return response.data
  } catch (error: any) {
    console.error("Error en getRequestById:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    })
    handleRequestError(error)
    return null
  }
}

const updateRequestStatus = async (
  updateData: UpdateStatusDto,
): Promise<{ success: boolean; message: string; updatedRequest?: RequestDto }> => {
  try {
    const url = `${API_URL}/requests/update-status`

    const formData = new FormData()
    formData.append("requestId", updateData.requestId.toString())
    formData.append("requestModuleStateId", updateData.requestModuleStateId.toString())

    const response = await axios.post(url, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })

    const success = response.status >= 200 && response.status < 300
    if (success && response.data) {
      requestStore.storeRequests([response.data])
    }

    cache.invalidateAll()

    return {
      success,
      message: "Estado actualizado con éxito",
      updatedRequest: response.data,
    }
  } catch (error: any) {
    console.error("Error en updateRequestStatus:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      request: error.request,
      config: error.config,
    })
    const message = error.response?.data || "Error desconocido al actualizar el estado"
    return {
      success: false,
      message,
    }
  }
}


const updateRequest = async (
  requestId: number,
  updateData: UpdateRequestDto,
): Promise<{ success: boolean; message: string }> => {
  try {
    const url = `${API_URL}/requests/update-request/${requestId}`

    const response = await axios.put(url, updateData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        "Content-Type": "application/json",
      },
    })

    const success = response.status >= 200 && response.status < 300
    const message = response.data || "Solicitud actualizada con éxito"

    requestStore.clear()
    cache.invalidateAll()

    return {
      success,
      message,
    }
  } catch (error: any) {
    console.error("Error en updateRequest:", error)
    console.error("Error detallado:", {
      message: error.message,
      response: error.response,
      request: error.request,
      config: error.config,
    })
    const message = error.response?.data || "Error desconocido al actualizar la solicitud"
    return {
      success: false,
      message,
    }
  }
}

const handleRequestError = (error: any) => {
  console.error("handleRequestError - error completo:", error)

  if (error.response) {
    console.error("Error de respuesta:", {
      status: error.response.status,
      data: error.response.data,
      headers: error.response.headers,
    })
    throw new Error(error.response.data || error.message)
  } else if (error.request) {
    console.error("Error de solicitud (no se recibió respuesta):", error.request)
    throw new Error("No se recibió respuesta del servidor")
  } else {
    console.error("Error de configuración:", error.message)
    throw new Error(error.message || "Error desconocido")
  }
}

export const requestService = {
  getRequestsByBroker,
  getRequestsByClient,
  getRequestCounters,
  getRequestById,
  updateRequestStatus,
  updateRequest,
}

export default requestService
